import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Book, Users, ArrowRight, Loader2, Headphones, Music2, Video } from 'lucide-react';
import { fetchChapters, fetchSlokas, fetchSpeakers, getErrorMessage } from '../api/client';
import ErrorMessage from './ErrorMessage';
import GitaLogo from './GitaLogo';
import DailyVerse from './DailyVerse';

const Home = () => {
  const [stats, setStats] = useState({
    totalChapters: 0,
    totalSlokas: 0,
    totalSpeakers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [chapters, slokas, speakers] = await Promise.all([
        fetchChapters(),
        fetchSlokas(),
        fetchSpeakers()
      ]);

      setStats({
        totalChapters: chapters.length,
        totalSlokas: slokas.length,
        totalSpeakers: speakers.length
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gita-saffron" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchStats} />;
  }

  return (
    <div className="space-y-10 sm:space-y-12">
      <section className="hero-section">
        <GitaLogo size={72} className="hero-logo mx-auto mb-4" />
        <h1 className="hero-title">Bhagavad Gita</h1>
        <p className="hero-subtitle">
          Explore timeless wisdom through chapters, verses, commentaries, and immersive
          continuous listening — crafted for study and devotion.
        </p>
        <div className="relative flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link to="/chapters" className="btn-primary w-full sm:w-auto">
            Explore Chapters
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
          <Link to="/listen" className="btn-secondary w-full sm:w-auto">
            <Headphones className="mr-2 w-4 h-4" />
            Listen
          </Link>
          <Link to="/listen/stream" className="btn-secondary w-full sm:w-auto">
            <Music2 className="mr-2 w-4 h-4" />
            Stream
          </Link>
          <Link to="/watch" className="btn-secondary w-full sm:w-auto">
            <Video className="mr-2 w-4 h-4" />
            Watch
          </Link>
        </div>
      </section>

      <DailyVerse />

      <div className="gita-divider">
        <span>Sacred Library</span>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
        <Link to="/chapters" className="stat-card">
          <div className="stat-icon bg-gita-saffron">
            <Book className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <p className="stat-value">{stats.totalChapters}</p>
          <p className="stat-label">Chapters</p>
        </Link>

        <Link to="/chapters" className="stat-card">
          <div className="stat-icon bg-gita-gold">
            <Book className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <p className="stat-value">{stats.totalSlokas}</p>
          <p className="stat-label">Slokas</p>
        </Link>

        <Link to="/speakers" className="stat-card">
          <div className="stat-icon bg-gita-orange">
            <Users className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <p className="stat-value">{stats.totalSpeakers}</p>
          <p className="stat-label">Speakers</p>
        </Link>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        <div className="card-premium">
          <h3 className="chapter-title mb-3">Chapter Explorer</h3>
          <p className="text-gita-muted mb-5 leading-relaxed">
            Navigate all 18 chapters grouped by yoga path — Karma, Bhakti, and Jnana —
            with summaries, meanings, and verse counts.
          </p>
          <Link to="/chapters" className="btn-primary">
            Browse Chapters
          </Link>
        </div>

        <div className="card-premium">
          <h3 className="chapter-title mb-3">Speaker Analysis</h3>
          <p className="text-gita-muted mb-5 leading-relaxed">
            Discover who speaks in the Gita and explore dialogues across chapters
            and spiritual traditions.
          </p>
          <Link to="/speakers" className="btn-primary">
            View Speakers
          </Link>
        </div>
      </section>

      <section className="card-premium">
        <h3 className="chapter-title mb-3">About This Application</h3>
        <p className="text-gita-muted leading-relaxed">
          A refined digital companion to the Bhagavad Gita — for students, researchers,
          and seekers. Read chapter by chapter, listen continuously with live verse
          transcripts, and study commentaries in English, Hindi, and Telugu.
        </p>
      </section>

      <div className="gita-divider">
        <span>References &amp; Inspirations</span>
      </div>

      <section className="card-premium">
        <p className="text-gita-muted text-sm mb-6 leading-relaxed">
          This application draws from the following sacred sources, teachers, and communities.
          We are grateful for their tireless seva in spreading the wisdom of the Gita.
        </p>
        <div className="references-grid">
          {[
            { label: 'Bhagavad Gita Foundation — Sri Gangadhar Sastry Garu', url: 'https://bhagavadgitafoundation.org/' },
            { label: 'ISKCON Hyderabad', url: 'https://iskconhyderabad.com/' },
            { label: 'Holy Bhagavad Gita — Sri Mukundananda Garu', url: 'https://www.holy-bhagavad-gita.org/' },
            { label: 'Holy Bhagavad Gita — YouTube Channel', url: 'https://www.youtube.com/@HOLYBHAGAVADGITA' },
            { label: 'Sri Pranavananda Das Garu — YouTube', url: 'https://www.youtube.com/@PranavanandaDas' },
            { label: 'Bhagavad Gita', url: 'https://bhagavadgita.org.in/' },
            { label: 'Sri Chaganti Koteswara Rao Garu — YouTube', url: 'https://www.youtube.com/@chagantikoteswararao2274' },
            { label: 'Sri Garikapati Garu — YouTube', url: 'https://www.youtube.com/@Garikipati_Offl' },
            { label: 'Sri R. P. Patnaik Garu — YouTube', url: 'https://www.youtube.com/@rppatnaikofficial' },
            { label: 'SGS Gita Foundation', url: 'https://www.sgsgitafoundation.org/' },
            { label: 'Rupanuga Bhajan Ashram', url: 'https://www.rupanugabhajanashram.com/' },
            { label: 'Kuku FM — Bhagavad Gita', url: 'https://kukufm.com/show/bhagavad-gita-11' },
            { label: 'Viveka Vani', url: 'https://vivekavani.com/' },
            { label: 'Vedanta Datasets — GitHub', url: 'https://github.com/atmabodha/Vedanta_Datasets' },
            { label: 'Ved Vyas', url: 'https://vedvyas.org/' },
            { label: 'Bhagavad Gita.io', url: 'https://bhagavadgita.io/' },
            { label: 'Free Gurukul', url: 'https://www.freegurukul.org/' },
            { label: 'Hindu Temples Guide', url: 'https://www.hindutemplesguide.com/' },
            { label: 'JKYog', url: 'https://jkyog.in/en/' },
            { label: 'Sri Janaki Ram Garu — YouTube', url: 'https://www.youtube.com/@JanakiRamCosmicTube' },
            { label: 'Sri Vikramaditya Garu — YouTube', url: 'https://www.youtube.com/@VikramAdityaOfficial' },
            { label: 'Gantasala Bhagavad Gita — YouTube', url: 'https://www.youtube.com/watch?v=wwq4ynLqjJ4&t=1756s' },
          ].map(({ label, url }, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="reference-item"
            >
              <span className="reference-num">{i + 1}</span>
              <span className="reference-label">{label}</span>
            </a>
          ))}
          <div className="reference-item reference-item--community">
            <span className="reference-num">23</span>
            <div>
              <span className="reference-label">WhatsApp Communities</span>
              <ul className="reference-community-list">
                <li>భగవద్గీత నేర్చుకుందాము</li>
                <li>భగవద్గీత-శ్లోకం-JKYog-TG10</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
